import inViewport from 'in-viewport';
import Player from '@vimeo/player';
import WistiaPlayerApiLoader from 'wistia-player-api-loader';
import YouTubePlayer from 'youtube-player';

export default class Video {
  constructor() {
    this.selector = '.c-video';

    this.$modal = '.c-modal';

    this.videoClasses = {
      common: '.video-external',
      vimeo: 'video-external--vimeo',
      youtube: 'video-external--youtube',
      wistia: 'video-external--wistia',
      playing: 'playing',
    };

    this.youtubeVideos = [];
    this.deferredYoutubeVideos = [];
    this.deferredClonedYoutubeVideos = [];
    this.vimeoVideos = [];
    this.wistiaVideos = [];

    this.bootstrap();
  }

  bootstrap() {
    this.init();
    this.setupDeferredYTVideos();
    this.setupModals();
    this.bindMuteToggles();
  }

  init() {
    const videoCollection = Array.prototype.slice.call(
      document.querySelectorAll(this.videoClasses.common)
    );

    if (videoCollection.length === 0) {
      return;
    }

    videoCollection.forEach((video, idx) => {

      const type = this.getVideoType(video.getAttribute('class'));

      if (type === 'vimeo') {
        this.vimeoVideos.push(this.handleVimeoVideos(video, idx));
      }

      if (type === 'youtube') {
        const youtubeVideo = this.handleYTVideos(video, idx);

        if (youtubeVideo) {
          this.youtubeVideos.push(youtubeVideo);
        }
      }

      if (type === 'wistia') {
        this.wistiaVideos.push(this.handleWistiaVideo(video, idx));
      }
    });

    this.vimeoVideos.forEach(video => {
      this.setAspectRatio(video);
    });

    this.youtubeVideos.forEach(video => {
      this.setAspectRatio(video);
    });

    this.playVideosInViewport(videoCollection);
  }

  setupModals() {
    this.modalEvents($(this.$modal));
  }

  /**
   * Wire up click-to-toggle-sound buttons rendered for autoplaying+muted videos.
   */
  bindMuteToggles() {
    const buttons = Array.prototype.slice.call(
      document.querySelectorAll('.c-video__mute-toggle')
    );

    buttons.forEach(button => {
      const wrapper = this.findVideoWrapper(button);
      const source = button.dataset.videoSource;

      button.addEventListener('click', event => {
        event.preventDefault();

        const nowMuted = button.dataset.muted !== 'true';

        if (this.setVideoMuted(wrapper, source, nowMuted)) {
          this.setMuteToggleState(button, nowMuted);
        }
      });

      if (source === 'file' && wrapper) {
        this.autoHideMuteToggleIfSilent(wrapper, button);
      }
    });
  }

  /**
   * For native file videos, hide the mute toggle if the browser detects no audio track.
   * @param {Node} wrapper
   * @param {Node} button
   */
  // eslint-disable-next-line class-methods-use-this
  autoHideMuteToggleIfSilent(wrapper, button) {
    const nativeVideo = wrapper.querySelector('.c-video-file__video');

    if (!nativeVideo) {
      return;
    }

    const check = () => {
      let hasAudio;

      if (typeof nativeVideo.mozHasAudio !== 'undefined') {
        hasAudio = nativeVideo.mozHasAudio;
      } else if (nativeVideo.audioTracks) {
        hasAudio = nativeVideo.audioTracks.length > 0;
      }

      if (hasAudio === false) {
        button.hidden = true;
      }
    };

    if (nativeVideo.readyState >= 1) {
      check();
    } else {
      nativeVideo.addEventListener('loadedmetadata', check, { once: true });
    }
  }

  /**
   * Find the .c-video wrapper for a mute-toggle button. For background
   * videos the button is a sibling of .c-background (a "cousin" of
   * .c-video), so fall back to searching the shared parent.
   * @param {Node} button
   * @return {Node|null}
   */
  findVideoWrapper(button) {
    return (
      button.closest(this.selector) ||
      (button.parentElement ? button.parentElement.querySelector(this.selector) : null)
    );
  }

  /**
   * Find the mute-toggle button for a .c-video wrapper. Mirrors
   * findVideoWrapper: normally the button is inside the wrapper, but for
   * background videos it's a sibling of .c-background instead.
   * @param {Node} wrapper
   * @return {Node|null}
   */
  findMuteToggle(wrapper) {
    if (!wrapper) {
      return null;
    }
    // Inline video: button is inside the wrapper.
    const inside = wrapper.querySelector('.c-video__mute-toggle');
    if (inside) return inside;
    // Background video: button is a direct sibling of .c-background, not nested
    // inside it. Walk up ancestors and look for a direct-child toggle at each level.
    let el = wrapper.parentElement;
    while (el && el !== document.body) {
      let toggle = null;
      for (let i = 0; i < el.children.length; i++) {
        if (el.children[i].classList.contains('c-video__mute-toggle')) {
          toggle = el.children[i];
          break;
        }
      }
      if (toggle) return toggle;
      el = el.parentElement;
    }
    return null;
  }

  /**
   * Update a mute-toggle button's visual/ARIA state to match the player.
   * @param {Node} button
   * @param {Boolean} muted
   */
  // eslint-disable-next-line class-methods-use-this
  setMuteToggleState(button, muted) {
    button.dataset.muted = String(muted);
    button.classList.toggle('is-muted', muted);
    button.setAttribute('aria-pressed', String(muted));
    button.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
  }

  /**
   * Find and sync a wrapper's mute-toggle button (if any) to a muted state.
   * @param {Node} wrapper
   * @param {Boolean} muted
   */
  syncMuteToggle(wrapper, muted) {
    const button = this.findMuteToggle(wrapper);

    if (button) {
      this.setMuteToggleState(button, muted);
    }
  }

  /**
   * Set the muted state on the underlying player for a given video source.
   * @param {Node} wrapper
   * @param {String} source
   * @param {Boolean} muted
   */
  setVideoMuted(wrapper, source, muted) {
    if (!wrapper) {
      return false;
    }

    if (source === 'file') {
      const nativeVideo = wrapper.querySelector('.c-video-file__video');

      if (!nativeVideo) {
        return false;
      }

      nativeVideo.muted = muted;
      return true;
    }

    const target = wrapper.querySelector(this.videoClasses.common);
    const id = target ? target.getAttribute('id') : null;

    if (!id) {
      return false;
    }

    if (source === 'vimeo') {
      const found = this.vimeoVideos.find(({ id: videoId }) => videoId === id);

      if (!found) {
        return false;
      }

      found.player.setMuted(muted);
      target.dataset.muted = String(muted);
      return true;
    }

    if (source === 'youtube') {
      const found = this.youtubeVideos.find(({ id: videoId }) => videoId === id);

      if (!found) {
        return false;
      }

      if (muted) {
        found.player.mute();
      } else {
        found.player.unMute();
      }
      target.dataset.muted = String(muted);
      return true;
    }

    if (source === 'wistia') {
      const wistiaVideo = window.Wistia && typeof window.Wistia.api === 'function'
        ? window.Wistia.api(id)
        : null;

      if (!wistiaVideo) {
        return false;
      }

      if (muted) {
        wistiaVideo.mute();
      } else {
        wistiaVideo.unmute();
      }
      target.dataset.muted = String(muted);
      return true;
    }

    return false;
  }

  /**
   * Get video type
   * @param {String} className
   * @return {String/Null}
   */
  getVideoType(className) {
    const { vimeo, youtube, wistia } = this.videoClasses;

    if (className.search(vimeo) !== -1) {
      return 'vimeo';
    }

    if (className.search(youtube) !== -1) {
      return 'youtube';
    }

    if (className.search(wistia) !== -1) {
      return 'wistia';
    }

    return null;
  }

  /**
   * Read a boolean data-attribute (data-* values are stored as '1'/'true' strings)
   * @param {Node} video
   * @param {String} key
   * @return {Boolean}
   */
  // eslint-disable-next-line class-methods-use-this
  datasetBool(video, key) {
    const value = video.dataset[key];

    return value === '1' || value === 'true';
  }

  /**
   * Should include poster
   * @param {Node} video
   */
  // eslint-disable-next-line class-methods-use-this
  includePoster(video) {
    const poster = video.dataset.getPoster;

    if (poster) {
      return true;
    }
  }

  /**
   * Get video thumb
   * @param {Object} video
   * @param {String} type
   */
  // eslint-disable-next-line class-methods-use-this
  fetchThumbnails(video) {
    const { id, player, type } = video;

    // If type is vimeo
    if (type === 'vimeo' && player) {
      const videoOriginalId = id.split('-')[1];

      player.ready().then(() => {
        $.ajax({
          type: 'GET',
          url: `https://vimeo.com/api/v2/video/${videoOriginalId}.json`,
          jsonp: 'callback',
          dataType: 'jsonp',
          success(data) {
            const thumbnailUrl = data[0].thumbnail_large;

            $(`#${id} .video__placeholder`).attr(
              'style',
              `background-image: url(${thumbnailUrl})`
            );
          },
        });
      });
    }

    // If type is youtube
    if (type === 'youtube' && player) {
      player.on('ready', data => {
        const videoId = data.target.playerInfo.videoData.video_id;
        const videoThumbnail = `//img.youtube.com/vi/${videoId}/hqdefault.jpg`;

        $(`#${id}`)
          .nextAll('.video__placeholder')
          .attr('style', `background-image: url(${videoThumbnail})`);
      });
    }
  }

  /**
   * Handle Vimeo Videos
   * @param {Node} video
   */
  handleVimeoVideos(video, idx) {
    if (!video) {
      return;
    }

    const wrapper = video.closest(this.selector);
    const playButton = video.querySelector('.btn-play');
    const id = video.dataset.videoId;
    const uniqueId = `player-${id}-${idx}`;
    const fetchPoster = this.includePoster(video);
    const autoplay = this.datasetBool(video, 'autoplay');
    const controls = 'controls' in video.dataset ? this.datasetBool(video, 'controls') : !autoplay;
    const loop = this.datasetBool(video, 'loop');
    const muted = 'muted' in video.dataset ? this.datasetBool(video, 'muted') : autoplay;
    if (!('controls' in video.dataset)) video.dataset.controls = controls ? '1' : '';
    if (!('muted' in video.dataset)) video.dataset.muted = muted ? '1' : '';

    // Set unique id
    video.setAttribute('id', uniqueId);

    // Init player
    const player = new Player(uniqueId, {
      id,
      muted,
      loop,
      controls,
    });

    if (playButton) {
      playButton.addEventListener('click', event => {
        event.preventDefault();
        wrapper.classList.add(this.videoClasses.playing);
        player.play();
      });
    }

    const videoObject = {
      id: uniqueId,
      player,
      type: 'vimeo',
    };

    if (fetchPoster) {
      this.fetchThumbnails(videoObject);
    }

    return videoObject;
  }

  /**
   * Check whether a YouTube video belongs to a cloned hero slide.
   * @param {Node} video
   * @return {Boolean}
   */
  // eslint-disable-next-line class-methods-use-this
  isClonedYTVideo(video) {
    const slide = video.closest('.b-af-hero .slick-slide');

    return Boolean(
      slide &&
      slide.classList.contains('slick-cloned')
    );
  }

  /**
   * Check whether a YouTube video belongs to a clone used for a wrap transition.
   * @param {Node} video
   * @return {Boolean}
   */
  // eslint-disable-next-line class-methods-use-this
  isWrapClonedYTVideo(video) {
    const slide = video.closest('.b-af-hero .slick-slide.slick-cloned');

    if (!slide) {
      return false;
    }

    const slider = slide.closest('.slick-slider');
    const slideIndex = Number(slide.getAttribute('data-slick-index'));
    const slideCount = slider.querySelectorAll('.slick-slide:not(.slick-cloned)').length;

    return slideIndex === -1 || slideIndex === slideCount;
  }

  /**
   * Defer YouTube players in inactive original hero slides.
   * @param {Node} video
   * @return {Boolean}
   */
  // eslint-disable-next-line class-methods-use-this
  shouldDeferYTVideo(video) {
    const slide = video.closest('.b-af-hero .slick-slide');

    return Boolean(
      slide &&
      !slide.classList.contains('slick-active')
    );
  }

  /**
   * Initialize deferred YouTube players as their original slide becomes active.
   */
  setupDeferredYTVideos() {
    if (
      this.deferredYoutubeVideos.length === 0 &&
      this.deferredClonedYoutubeVideos.length === 0
    ) {
      return;
    }

    const $sliders = $('.b-af-hero .slick-slider');

    $sliders.off('beforeChange.youtubeVideo afterChange.youtubeVideo');

    $sliders.on('beforeChange.youtubeVideo', (event, slick, currentSlide, nextSlide) => {
      let wrapSlideIndex = null;

      if (currentSlide === slick.slideCount - 1 && nextSlide === 0) {
        wrapSlideIndex = slick.slideCount;
      }

      if (currentSlide === 0 && nextSlide === slick.slideCount - 1) {
        wrapSlideIndex = -1;
      }

      if (wrapSlideIndex === null) {
        return;
      }

      const pendingVideos = [];

      this.deferredClonedYoutubeVideos.forEach(({ video, idx }) => {
        const slide = video.closest('.slick-cloned');
        const slider = slide ? slide.closest('.slick-slider') : null;
        const slideIndex = slide
          ? Number(slide.getAttribute('data-slick-index'))
          : null;

        if (slider !== event.currentTarget || slideIndex !== wrapSlideIndex) {
          pendingVideos.push({ video, idx });
          return;
        }

        const videoObject = this.handleYTVideos(video, idx, true);

        if (videoObject) {
          this.youtubeVideos.push(videoObject);
          this.setAspectRatio(videoObject);
        }
      });

      this.deferredClonedYoutubeVideos = pendingVideos;

      if (this.deferredClonedYoutubeVideos.length === 0) {
        $sliders.off('beforeChange.youtubeVideo');
      }
    });

    $sliders.on('afterChange.youtubeVideo', () => {
      const pendingVideos = [];
      let initializedVideo = false;

      this.deferredYoutubeVideos.forEach(({ video, idx }) => {
        if (this.shouldDeferYTVideo(video)) {
          pendingVideos.push({ video, idx });
          return;
        }

        const videoObject = this.handleYTVideos(video, idx);

        if (videoObject) {
          this.youtubeVideos.push(videoObject);
          this.setAspectRatio(videoObject);
          initializedVideo = true;
        }
      });

      this.deferredYoutubeVideos = pendingVideos;

      if (this.deferredYoutubeVideos.length === 0) {
        $sliders.off('afterChange.youtubeVideo');
      }

      if (initializedVideo) {
        // Wait until Slick finishes positioning the newly active slide.
        window.requestAnimationFrame(() => {
          $(window).trigger('scroll');
        });
      }
    });
  }

  /**
   * Handle YT
   * @param {Node} video
   */
  handleYTVideos(video, idx, initializeClone) {
    if (!video) {
      return;
    }

    if (this.isClonedYTVideo(video) && !initializeClone) {
      if (this.isWrapClonedYTVideo(video)) {
        this.deferredClonedYoutubeVideos.push({ video, idx });
      }

      return;
    }

    if (!initializeClone && this.shouldDeferYTVideo(video)) {
      this.deferredYoutubeVideos.push({ video, idx });
      return;
    }

    const wrapper = video.closest(this.selector);
    const id = video.dataset.videoId;
    const uniqueId = `player-${id}-${idx}`;
    const initialHTML = video.innerHTML;
    const fetchPoster = this.includePoster(video);
    const autoplay = this.datasetBool(video, 'autoplay');
    const controls = 'controls' in video.dataset ? this.datasetBool(video, 'controls') : !autoplay;
    const loop = this.datasetBool(video, 'loop');
    const muted = 'muted' in video.dataset ? this.datasetBool(video, 'muted') : autoplay;
    if (!('controls' in video.dataset)) video.dataset.controls = controls ? '1' : '';
    if (!('muted' in video.dataset)) video.dataset.muted = muted ? '1' : '';

    // Set unique id
    video.setAttribute('id', uniqueId);

    // Init player
    const player = YouTubePlayer(uniqueId, {
      videoId: id,
      playerVars: {
        controls: controls ? 1 : 0,
        disablekb: autoplay ? 1 : 0,
        // YouTube's loop param only takes effect on an embedded player when
        // playlist is also set to the same video id -- a common API gotcha.
        ...(loop ? { loop: 1, playlist: id } : {}),
      },
    });

    // Respect the muted setting instead of always forcing mute on autoplay.
    if (muted) {
      player.mute();
    }

    const videoObject = {
      id: uniqueId,
      isActive: false,
      isPlaying: false,
      // Store button reference once at init so we never need to re-traverse the DOM.
      muteButton: this.findMuteToggle(wrapper),
      player,
      type: 'youtube',
    };

    // Listen for state changes to manage user pause/play actions
    player.on('stateChange', (event) => {
      if (!wrapper) return;

      // 1 = playing, 3 = buffering
      videoObject.isPlaying = event.data === 1 || event.data === 3;

      // 2 = PAUSED
      if (event.data === 2) {
        // If we paused via scroll logic, ignore this pause as "user"
        if (wrapper.getAttribute('data-autopausing') === 'true') {
          wrapper.removeAttribute('data-autopausing');
          return;
        }

        // Otherwise it's a real user pause
        wrapper.setAttribute('data-user-paused', 'true');
      }

      // 1 = PLAYING
      if (event.data === 1) {
        wrapper.removeAttribute('data-user-paused');
        wrapper.removeAttribute('data-autopausing');
        // Sync toggle to real browser-enforced state after playback starts.
        setTimeout(() => {
          player.isMuted().then(actuallyMuted => {
            if (!videoObject.muteButton) videoObject.muteButton = this.findMuteToggle(wrapper);
            if (videoObject.muteButton) this.setMuteToggleState(videoObject.muteButton, actuallyMuted);
          }).catch(() => {});
        }, 300);
      }
    });

    video.insertAdjacentHTML('afterend', initialHTML);

    wrapper.addEventListener('click', event => {
      event.preventDefault();

      if (event.target.matches('.btn-play, .btn-play *')) {
        wrapper.classList.add(this.videoClasses.playing);

        // Clear flags (manual user intent)
        wrapper.removeAttribute('data-user-paused');
        wrapper.removeAttribute('data-autopausing');

        player.playVideo();
      }
    });

    if (fetchPoster) {
      this.fetchThumbnails(videoObject);
    }

    return videoObject;
  }

  /**
   * Handle Wistia
   * @param {Node} video
   */
  // eslint-disable-next-line class-methods-use-this
  handleWistiaVideo(video, idx) {
    if (!video) {
      return;
    }
    const id = video.dataset.videoId;
    const uniqueId = `player-${id}-${idx}`;
    const autoplay = this.datasetBool(video, 'autoplay');
    const controls = 'controls' in video.dataset ? this.datasetBool(video, 'controls') : !autoplay;
    const loop = this.datasetBool(video, 'loop');
    const muted = 'muted' in video.dataset ? this.datasetBool(video, 'muted') : autoplay;
    if (!('controls' in video.dataset)) video.dataset.controls = controls ? '1' : '';
    if (!('muted' in video.dataset)) video.dataset.muted = muted ? '1' : '';

    // Set unique id
    video.setAttribute('id', uniqueId);

    WistiaPlayerApiLoader.load(Wistia => {
      Wistia.embed(id, {
        container: uniqueId,
        videoFoam: true,
        controlsVisibleOnLoad: controls,
        playButton: controls,
        playbar: controls,
        settingsControl: controls,
        volumeControl: controls,
        fullscreenButton: controls,
        playbackRateControl: controls,
        qualityControl: controls,
        smallPlayButton: controls,
        endVideoBehavior: loop ? 'loop' : 'default',
        muted,
      });
    });

    return uniqueId;
  }

  /**
   * Set padding according to video ratio
   * @param {Object} video
   */
  setAspectRatio({ id, player, type }) {
    if (type === 'vimeo') {
      player.ready().then(() => {
        const element = document.getElementById(id);
        const ratioPadding = this.getAspectRatio(player.element);

        element.classList.add('video--responsive');
        element.style.paddingTop = ratioPadding;
      });
    }

    if (type === 'youtube') {
      player.on('ready', data => {
        const element = document.getElementById(id);
        const wrap = element.closest(this.selector);
        const backgroundVideo = wrap
          ? wrap.closest('.c-background-video, .c-background__media')
          : null;
        const ratioPadding = this.getAspectRatio(data.target.f);

        if (backgroundVideo) {
          wrap.classList.remove('video--responsive');
          wrap.style.paddingTop = '';

          return;
        }

        wrap.classList.add('video--responsive');
        wrap.style.paddingTop = ratioPadding;
      });
    }
  }

  /**
   * Get ratio from element
   * @param {Node} video
   */
  // eslint-disable-next-line class-methods-use-this
  getAspectRatio(video) {
    if (video) {
      const width = video.getAttribute('width');
      const height = video.getAttribute('height');

      return `${(height / width) * 100}%`;
    }

    return '56.25%';
  }

  /**
   * Play videos in viewport
   * @param {Array} videos
   */
  playVideosInViewport(videos) {
    const handleViewportPlayback = () => {
      videos.forEach(video => {
        const playInViewport = video.dataset.autoplay === '1' || video.dataset.autoplay === 'true';
        const autoStop = video.dataset.autopause === '1' || video.dataset.autopause === 'true';
        const id = video.getAttribute('id');
        const type = this.getVideoType(video.getAttribute('class'));
        const players = this[`${type}Videos`];

        if (type === 'vimeo') {
          players.forEach(({ id: videoId, player }) => {
            player.ready().then(() => {
              if (videoId === id) {
                const element = document.getElementById(videoId);
                const wrap = element ? element.closest(this.selector) : null;
                const slide = element ? element.closest('.slick-slide') : null;
                const isActive = !slide || slide.classList.contains('slick-active');

                // Play vimeo only if is set to autoplay, slide is active, and is in viewport
                if (playInViewport && isActive && inViewport(video)) {
                  player.setMuted(this.datasetBool(video, 'muted'));
                  if (wrap) {
                    wrap.classList.add(this.videoClasses.playing);
                  }

                  player.play();
                }

                // Pause vimeo if slide is inactive, or out of viewport with autopause
                if (!isActive || (!inViewport(video) && autoStop)) {
                  player.pause();

                  if (wrap) {
                    wrap.classList.remove(this.videoClasses.playing);
                  }
                }
              }
            });
          });
        }

        if (type === 'youtube') {
          players.forEach(videoObject => {
            const { id: videoId, player } = videoObject;

            if (videoId === id) {
              const element = document.getElementById(videoId);
              const wrap = element ? element.closest(this.selector) : null;

              if (!wrap) {
                return;
              }

              const slide = wrap.closest('.slick-slide');
              const isActive = !slide || slide.classList.contains('slick-active');
              const becameActive = isActive && !videoObject.isActive;

              videoObject.isActive = isActive;

              if (becameActive) {
                wrap.removeAttribute('data-user-paused');
                wrap.removeAttribute('data-autopausing');
              }

              const userPaused = wrap.hasAttribute('data-user-paused');
              const isInViewport = inViewport(wrap);

              // Play youtube only if is set to autoplay, slide is active, and is in viewport
              if (playInViewport && isActive && inViewport(wrap) && !userPaused) {
                if (becameActive || !videoObject.isPlaying) {
                  if (this.datasetBool(video, 'muted')) {
                    player.mute();
                  } else {
                    player.unMute();
                  }
                }
                wrap.classList.add(this.videoClasses.playing);
                if (!videoObject.isPlaying) {
                  player.playVideo();
                }
              }

              // Always pause inactive slides; also pause when out of viewport with autopause
              if (!isActive || (!isInViewport && autoStop)) {
                if (videoObject.isPlaying) {
                  wrap.setAttribute('data-autopausing', 'true');
                  player.pauseVideo();
                }
                wrap.classList.remove(this.videoClasses.playing);
              }
            }
          });
        }

        if (type === 'wistia') {
          players.forEach(videoId => {
            const wistiaVideo = window.Wistia && typeof window.Wistia.api === 'function'
              ? window.Wistia.api(videoId)
              : null;

            if (!wistiaVideo) {
              return;
            }

            if (id === videoId) {
              const slide = video.closest('.slick-slide');
              const isActive = !slide || slide.classList.contains('slick-active');

              // Play Wistia only if it is set to autoplay, slide is active, and is in viewport.
              if (playInViewport && isActive && inViewport(video)) {
                if (this.datasetBool(video, 'muted')) {
                  wistiaVideo.mute();
                } else {
                  wistiaVideo.unmute();
                }
                wistiaVideo.play();
              }

              // Pause Wistia if slide is inactive, or out of viewport with autopause.
              if (!isActive || (!inViewport(video) && autoStop)) {
                wistiaVideo.pause();
              }
            }
          });
        }
      });
    };

    // Run once on init so autoplay works without requiring user scroll,
    // then keep syncing on load, scroll and resize.
    const handleSlickFileVideoPlayback = () => {
      const fileVideos = Array.prototype.slice.call(
        document.querySelectorAll('.slick-slide .c-video-file__video')
      );

      fileVideos.forEach(nativeVideo => {
        const slide = nativeVideo.closest('.slick-slide');
        const isActive = !slide || slide.classList.contains('slick-active');

        if (isActive && nativeVideo.hasAttribute('autoplay')) {
          if (nativeVideo.paused) {
            nativeVideo.play().catch(() => {});
          }
        } else if (!isActive && !nativeVideo.paused) {
          nativeVideo.pause();
          nativeVideo.currentTime = 0;
        }
      });
    };

    handleViewportPlayback();
    handleSlickFileVideoPlayback();
    $(window).on('load scroll resize', handleViewportPlayback);
    $(window).on('load scroll resize', handleSlickFileVideoPlayback);

    // Re-sync immediately on every slick slide change rather than relying
    // solely on the indirect scroll trigger from hero.js's afterChange.
    // off() first keeps this idempotent if Video is ever instantiated more than once.
    $(document).off('afterChange.videoPlaybackSync').on('afterChange.videoPlaybackSync', '.slick-slider', () => {
      window.requestAnimationFrame(handleViewportPlayback);
      window.requestAnimationFrame(handleSlickFileVideoPlayback);
    });
  }

  /**
   * Add listeners for modals with videos
   * @param {jQueryObject} $modal
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  modalEvents($modal) {
    $modal
    // eslint-disable-next-line sonarjs/cognitive-complexity
      .on('shown.bs.modal', ({ currentTarget }) => {
        const $video = $(currentTarget).find(this.videoClasses.common);

        if ($video.length === 0) {
          return;
        }

        const currVideoId = $video.attr('id');
        const playInViewport = $video.data('autoplay');
        const type = this.getVideoType($video.attr('class'));
        const muted = this.datasetBool($video[0], 'muted');

        if (type === 'youtube' && playInViewport) {
          this.youtubeVideos.forEach(({ id, player }) => {
            if (currVideoId === id) {
              const wrap = document.getElementById(id).closest(this.selector);

              if (muted) {
                player.mute();
              } else {
                player.unMute();
              }
              this.syncMuteToggle(wrap, muted);
              wrap.querySelector('.btn-play').click();
            }
          });
        }

        if (type === 'vimeo' && playInViewport) {
          this.vimeoVideos.forEach(({ id, player }) => {
            if (currVideoId === id) {
              const wrap = document.getElementById(id).closest(this.selector);

              player.setMuted(muted);
              this.syncMuteToggle(wrap, muted);
              document.getElementById(id).querySelector('.btn-play').click();
            }
          });
        }

        if (type === 'wistia' && playInViewport) {
          this.wistiaVideos.forEach(id => {
            if (currVideoId === id) {
              const wistiaVideo = window.Wistia.api(id);
              const wrap = document.getElementById(id).closest(this.selector);

              if (muted) {
                wistiaVideo.mute();
              } else {
                wistiaVideo.unmute();
              }
              this.syncMuteToggle(wrap, muted);
              wistiaVideo.play();
            }
          });
        }
      })
      .on('hidden.bs.modal', ({ currentTarget }) => {
        const $video = $(currentTarget).find(this.videoClasses.common);

        if ($video.length === 0) {
          return;
        }

        const type = this.getVideoType($video.attr('class'));

        if (type === 'vimeo') {
          this.vimeoVideos.forEach(({ player }) => {
            player.pause();
          });
        }

        if (type === 'youtube') {
          this.youtubeVideos.forEach(({ player }) => {
            player.stopVideo();
          });
        }

        if (type === 'wistia') {
          this.wistiaVideos.forEach(id => {
            const wistiaVideo = window.Wistia.api(id);

            wistiaVideo.pause();
          });
        }
      });
  }
}



// WEBPACK FOOTER //
// ./scripts/components/video.js